import React, { Component } from "react";
import "./GamificationToastWrapper.css";

// ── timing constants ─────────────────────────────────────────────────────────
const TOAST_DURATION = 4200; // ms visible before exit animation
const STAGGER_MS = 320; // delay between sequential toasts

// Pick an icon bucket based on message content
function pickIcon(msg) {
  if (msg.includes("👑")) return "👑";
  if (msg.includes("💎")) return "💎";
  if (msg.includes("⚡")) return "⚡";
  if (msg.includes("🧠")) return "🧠";
  if (msg.includes("🏅")) return "🏅";
  if (msg.includes("📚")) return "📚";
  if (msg.includes("Level up")) return "🏅";
  return "✨";
}

// ── wrapper ──────────────────────────────────────────────────────────────────
// Displays a stack of staggered, auto-dismissing toasts for gamification
// messages (e.g. the `messages` array returned by triggergamificationevent.webapi).
// Pass a new `messages` array via props whenever a new batch should be shown —
// the wrapper tracks which array it already displayed so it won't re-fire on
// unrelated re-renders.
export default class GamificationToastWrapper extends Component {
  constructor(props) {
    super(props);
    this.state = {
      toasts: [],
    };
    this.toastIdCounter = 0;
    this.timers = {}; // toastId -> [timeoutId, timeoutId, timeoutId]
    this.lastMessagesRef = null;
  }

  componentDidMount() {
    this.maybeEnqueueFromProps();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.messages !== this.props.messages) {
      this.maybeEnqueueFromProps();
    }
  }

  componentWillUnmount() {
    Object.values(this.timers).forEach((ids) => ids.forEach(clearTimeout));
    this.timers = {};
  }

  maybeEnqueueFromProps = () => {
    const { messages } = this.props;
    if (
      messages &&
      messages.length > 0 &&
      messages !== this.lastMessagesRef
    ) {
      this.lastMessagesRef = messages;
      this.enqueueMessages(messages);
    }
  };

  enqueueMessages = (messages) => {
    const newToasts = messages.map((msg, i) => ({
      id: ++this.toastIdCounter,
      message: msg,
      index: i,
      phase: "enter",
    }));

    this.setState(
      (prev) => ({ toasts: [...prev.toasts, ...newToasts] }),
      () => {
        newToasts.forEach((toast) => this.scheduleToastTimers(toast));
      },
    );
  };

  scheduleToastTimers = (toast) => {
    // stagger entry
    const enterTimer = setTimeout(
      () => this.setToastPhase(toast.id, "idle"),
      toast.index * STAGGER_MS,
    );

    // start exit after full display time + stagger
    const exitTimer = setTimeout(
      () => this.setToastPhase(toast.id, "exit"),
      toast.index * STAGGER_MS + TOAST_DURATION,
    );

    // remove from DOM after exit animation (~420ms)
    const doneTimer = setTimeout(
      () => this.removeToast(toast.id),
      toast.index * STAGGER_MS + TOAST_DURATION + 420,
    );

    this.timers[toast.id] = [enterTimer, exitTimer, doneTimer];
  };

  setToastPhase = (id, phase) => {
    this.setState((prev) => ({
      toasts: prev.toasts.map((t) => (t.id === id ? { ...t, phase } : t)),
    }));
  };

  removeToast = (id) => {
    if (this.timers[id]) {
      this.timers[id].forEach(clearTimeout);
      delete this.timers[id];
    }
    this.setState((prev) => ({
      toasts: prev.toasts.filter((t) => t.id !== id),
    }));
  };

  render() {
    const { toasts } = this.state;

    return (
      <>
        {this.props.children}

        {/* Portal-style overlay — sits above everything */}
        {toasts.length > 0 && (
          <div className="gamToastStage" aria-live="polite">
            {toasts.map((t) => (
              <div
                className={`gamToast gamToast--${t.phase}`}
                role="alert"
                key={t.id}
              >
                <span className="gamToast__icon">{pickIcon(t.message)}</span>
                <span className="gamToast__text">{t.message}</span>
                <div className="gamToast__progress">
                  <div
                    className="gamToast__progressBar"
                    style={{ animationDelay: `${t.index * STAGGER_MS}ms` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }
}
